import type { Ref } from 'vue';
import { computed } from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import type {
  IExtendedTableDataResponse,
  ITableState,
} from '@modules/tables/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextQuery } from '@modules/core/services/query';
import { FINANCE_PERMISSIONS } from '@modules/finance/constants';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { keepPreviousData } from '@tanstack/vue-query';

import type {
  IFinanceMonthAdditionalTableData,
  IFinanceMonthListItem,
} from '../types';
import {
  FINANCE_MONTH_LIST_API_URL_ADMIN,
  FINANCE_MONTH_LIST_API_URL_AFFILIATE,
  FINANCE_MONTH_LIST_QUERY_KEY,
} from '../constants';

export default function useFinanceMonthListQuery(
  tableState: Ref<ITableState>,
  dateOfMonth: Ref<string>,
): IContextQueryResult<IExtendedTableDataResponse<IFinanceMonthListItem, IFinanceMonthAdditionalTableData>, Error> {
  const { isAdminNamespace } = useCurrentNamespace();
  const { fetchPost } = useRestClient();

  return useContextQuery<IExtendedTableDataResponse<IFinanceMonthListItem, IFinanceMonthAdditionalTableData>>({
    queryKey: [FINANCE_MONTH_LIST_QUERY_KEY, dateOfMonth, tableState],
    queryFn: ({ signal }) => fetchPost<IExtendedTableDataResponse<IFinanceMonthListItem, IFinanceMonthAdditionalTableData>>(
      isAdminNamespace.value ? FINANCE_MONTH_LIST_API_URL_ADMIN : FINANCE_MONTH_LIST_API_URL_AFFILIATE,
      {
        ...tableState.value,
        date_of_payment: dateOfMonth.value,
      },
      {
        signal,
        permission: FINANCE_PERMISSIONS.SHOW,
      },
    ),
    placeholderData: keepPreviousData,
    enabled: computed(() => !!dateOfMonth.value),
  });
}
